import { type FC, useEffect, useRef } from "react";
import {
    useDateRangePicker,
    useDateField,
    useDateSegment,
    useLocale,
    useRangeCalendar,
    useCalendarGrid,
    useCalendarCell,
    useButton,
} from "react-aria";
import { useDateRangePickerState, useDateFieldState, useRangeCalendarState } from "react-stately";
import { createCalendar, getWeeksInMonth, isSameDay, type CalendarDate } from "@internationalized/date";
import type { DateSegment as DateSegmentType, DateFieldState } from "@react-stately/datepicker";
import type { RangeCalendarState } from "@react-stately/calendar";
import type { RangeValue } from "@react-types/shared";
import type { AriaButtonProps } from "react-aria";
import { cn } from "@/components/podkit/lib/cn";
import { IconCalendar } from "@/assets/icons/geist/IconCalendar";

type DateRangePickerFieldProps = {
    value: { start: CalendarDate; end: CalendarDate };
    onChange: (value: { start: CalendarDate; end: CalendarDate }) => void;
    maxValue?: CalendarDate;
    /** Exposes react-aria's validation state so the parent can disable actions. */
    onValidationChange?: (isInvalid: boolean) => void;
};

export const DateRangePickerField: FC<DateRangePickerFieldProps> = ({
    value,
    onChange,
    maxValue,
    onValidationChange,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const startFieldRef = useRef<HTMLDivElement>(null);
    const endFieldRef = useRef<HTMLDivElement>(null);

    const handleChange = (newValue: RangeValue<CalendarDate> | null) => {
        if (newValue) {
            onChange(newValue);
        }
    };

    const state = useDateRangePickerState({
        value,
        onChange: handleChange,
        maxValue,
        granularity: "day",
    });

    const { groupProps, startFieldProps, endFieldProps, buttonProps, dialogProps, calendarProps } = useDateRangePicker(
        { value, onChange: handleChange, maxValue, granularity: "day", "aria-label": "Custom date range" },
        state,
        ref,
    );

    const isInvalid = state.isInvalid;

    useEffect(() => {
        onValidationChange?.(isInvalid);
    }, [isInvalid, onValidationChange]);

    return (
        <div className="relative" data-testid="date-range-picker">
            <div
                {...groupProps}
                ref={ref}
                className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg border bg-surface-pure px-3 py-1.5",
                    isInvalid ? "border-red-500" : "border-border-base",
                )}
            >
                <DateField {...startFieldProps} fieldRef={startFieldRef} />
                <span className="text-sm text-content-tertiary" aria-hidden="true">
                    &rarr;
                </span>
                <DateField {...endFieldProps} fieldRef={endFieldRef} />
                <CalendarButton {...buttonProps} />
            </div>
            {state.isOpen && (
                <div
                    {...(dialogProps as React.HTMLAttributes<HTMLDivElement>)}
                    className="absolute left-0 top-full z-50 mt-1 rounded-lg border border-border-base bg-surface-pure p-3 shadow-lg"
                >
                    <RangeCalendar {...(calendarProps as unknown as Parameters<typeof useRangeCalendarState>[0])} />
                </div>
            )}
        </div>
    );
};

const CalendarButton: FC<AriaButtonProps> = (props) => {
    const ref = useRef<HTMLButtonElement>(null);
    const { buttonProps } = useButton(props, ref);
    return (
        <button
            {...buttonProps}
            ref={ref}
            className="focus-visible:ring-ring ml-1 rounded p-0.5 text-content-secondary hover:bg-surface-secondary hover:text-content-primary focus:outline-none focus-visible:ring-2"
        >
            <IconCalendar size="sm" className="h-4 w-4" />
        </button>
    );
};

// Accept the full props from useDateRangePicker's field props
type DateFieldProps = Record<string, unknown> & {
    fieldRef: React.RefObject<HTMLDivElement | null>;
};

const DateField: FC<DateFieldProps> = ({ fieldRef, ...props }) => {
    const { locale } = useLocale();
    const state = useDateFieldState({
        ...(props as unknown as Parameters<typeof useDateFieldState>[0]),
        locale,
        createCalendar,
    });

    const { fieldProps } = useDateField(
        props as unknown as Parameters<typeof useDateField>[0],
        state,
        fieldRef as unknown as React.RefObject<HTMLDivElement>,
    );

    return (
        <div {...fieldProps} ref={fieldRef as React.Ref<HTMLDivElement>} className="inline-flex items-center">
            {state.segments.map((segment, i) => (
                <Segment key={i} segment={segment} state={state} />
            ))}
        </div>
    );
};

type SegmentProps = {
    segment: DateSegmentType;
    state: DateFieldState;
};

const Segment: FC<SegmentProps> = ({ segment, state }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { segmentProps } = useDateSegment(segment, state, ref as unknown as React.RefObject<HTMLDivElement>);

    return (
        <div
            {...segmentProps}
            ref={ref}
            className={cn(
                "rounded px-0.5 text-sm tabular-nums outline-none",
                segment.type === "literal" ? "text-content-tertiary" : "text-content-primary",
                segment.isEditable && "focus:bg-surface-button-primary-accent focus:text-content-invert",
            )}
        >
            {segment.text}
        </div>
    );
};

// --- Calendar ---

const RangeCalendar: FC<Parameters<typeof useRangeCalendarState>[0]> = (props) => {
    const ref = useRef<HTMLDivElement>(null);
    const { locale } = useLocale();
    const state = useRangeCalendarState({
        ...(props as unknown as Parameters<typeof useRangeCalendarState>[0]),
        locale,
        createCalendar,
    });

    const { calendarProps, prevButtonProps, nextButtonProps, title } = useRangeCalendar(
        props as unknown as Parameters<typeof useRangeCalendar>[0],
        state,
        ref,
    );

    return (
        <div {...calendarProps} ref={ref} className="w-[280px]">
            <div className="mb-2 flex items-center justify-between">
                <CalendarNavButton {...prevButtonProps}>&lsaquo;</CalendarNavButton>
                <span className="text-sm font-medium text-content-primary">{title}</span>
                <CalendarNavButton {...nextButtonProps}>&rsaquo;</CalendarNavButton>
            </div>
            <CalendarGrid state={state} />
        </div>
    );
};

const CalendarNavButton: FC<AriaButtonProps & { children: React.ReactNode }> = (props) => {
    const ref = useRef<HTMLButtonElement>(null);
    const { buttonProps } = useButton(props, ref);
    return (
        <button
            {...buttonProps}
            ref={ref}
            className="focus-visible:ring-ring flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-secondary hover:text-content-primary focus:outline-none focus-visible:ring-2 disabled:opacity-30"
        >
            {props.children}
        </button>
    );
};

type CalendarGridProps = {
    state: RangeCalendarState;
};

const CalendarGrid: FC<CalendarGridProps> = ({ state }) => {
    const { locale } = useLocale();
    const { gridProps, headerProps, weekDays } = useCalendarGrid({}, state);

    const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

    return (
        <table {...gridProps} className="w-full border-collapse">
            <thead {...headerProps}>
                <tr>
                    {weekDays.map((day, i) => (
                        <th key={i} className="pb-1 text-center text-xs font-normal text-content-tertiary">
                            {day}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: weeksInMonth }, (_, weekIndex) => (
                    <tr key={weekIndex}>
                        {state
                            .getDatesInWeek(weekIndex)
                            .map((date, i) =>
                                date ? <CalendarCell key={i} state={state} date={date} /> : <td key={i} />,
                            )}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

type CalendarCellProps = {
    state: RangeCalendarState;
    date: CalendarDate;
};

const CalendarCell: FC<CalendarCellProps> = ({ state, date }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { cellProps, buttonProps, isSelected, isOutsideVisibleRange, isDisabled, formattedDate, isInvalid } =
        useCalendarCell({ date }, state, ref);

    const isRangeStart = state.highlightedRange ? isSameDay(date, state.highlightedRange.start) : false;
    const isRangeEnd = state.highlightedRange ? isSameDay(date, state.highlightedRange.end) : false;
    const isInRange = isSelected && !isRangeStart && !isRangeEnd;

    return (
        <td {...cellProps} className="relative p-0 text-center">
            <div
                {...buttonProps}
                ref={ref}
                hidden={isOutsideVisibleRange}
                className={cn(
                    "relative z-10 flex h-8 w-full cursor-pointer items-center justify-center rounded text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isDisabled && "cursor-default text-content-tertiary opacity-40",
                    !isSelected && !isDisabled && "text-content-primary hover:bg-surface-secondary",
                    (isRangeStart || isRangeEnd) && "bg-surface-button-primary-accent text-content-invert",
                    isInRange && "rounded-none bg-surface-button-primary-accent/15 text-content-primary",
                    isInvalid && "text-red-500",
                )}
            >
                {formattedDate}
            </div>
        </td>
    );
};
